import React, { useState } from 'react';
import { ethers } from 'ethers';
import { getGovernanceSignerContract } from '../contracts/contract';
import { useWallet } from '../contexts/WalletContext';
import { useGovernance } from '../contexts/GovernanceContext';
import { useNetwork } from '../contexts/NetworkContext';
import { trustlineService } from '../services/trustline';
import './CreateProposalForm.css';

interface CreateProposalFormProps {
  onClose: () => void;
  onProposalCreated: () => void;
}

const CreateProposalForm: React.FC<CreateProposalFormProps> = ({ onClose, onProposalCreated }) => {
  const { signer, account } = useWallet();
  const { contractAddress } = useGovernance();
  const { currentNetwork } = useNetwork();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [formData, setFormData] = useState({
    description: '',
    targets: [''],
    values: ['0'],
    calldatas: ['']
  });

  const handleInputChange = (field: string, value: string, index?: number) => {
    if (index !== undefined) {
      const newArray = [...formData[field as keyof typeof formData] as string[]];
      newArray[index] = value;
      setFormData(prev => ({ ...prev, [field]: newArray }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const addAction = () => {
    setFormData(prev => ({
      ...prev,
      targets: [...prev.targets, ''],
      values: [...prev.values, '0'],
      calldatas: [...prev.calldatas, '']
    }));
  };

  const removeAction = (index: number) => {
    if (formData.targets.length > 1) {
      setFormData(prev => ({
        ...prev,
        targets: prev.targets.filter((_, i) => i !== index),
        values: prev.values.filter((_, i) => i !== index),
        calldatas: prev.calldatas.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signer || !contractAddress) return;

    try {
      setLoading(true);
      setError('');

      // Validate form data
      if (!formData.description.trim()) {
        throw new Error('Description is required');
      }

      const validTargets = formData.targets.filter(target => target.trim());
      const validValues = formData.values.filter((_, i) => formData.targets[i].trim());
      const validCalldatas = formData.calldatas.filter((_, i) => formData.targets[i].trim());

      if (validTargets.length === 0) {
        throw new Error('At least one target is required');
      }

      // Validate addresses
      for (const target of validTargets) {
        if (!ethers.isAddress(target)) {
          throw new Error(`Invalid address: ${target}`);
        }
      }

      // Validate values
      for (const value of validValues) {
        if (isNaN(Number(value)) || Number(value) < 0) {
          throw new Error('All values must be valid non-negative numbers');
        }
      }

      // Validate and normalize calldatas
      const normalizedCalldatas = validCalldatas.map(calldata => {
        if (!calldata || calldata.trim() === '') {
          return '0x'; // Empty calldata should be '0x'
        }
        if (!calldata.startsWith('0x')) {
          throw new Error('Calldata must be valid hex string starting with 0x');
        }
        return calldata;
      });

      // Trustline validation for create proposal
      if (currentNetwork && account) {
        const isApproved = await trustlineService.validateCreateProposal(
          currentNetwork.chainId,
          account,
          contractAddress,
          validTargets,
          validValues.map(v => ethers.parseEther(v.toString()).toString()),
          normalizedCalldatas,
          formData.description
        );

        if (!isApproved) {
          throw new Error('Create proposal transaction not approved by Trustline compliance check');
        }
      }

      const contract = getGovernanceSignerContract(signer, contractAddress);
      
      const tx = await contract.propose(
        validTargets,
        validValues.map(v => ethers.parseEther(v.toString())),
        normalizedCalldatas,
        formData.description
      );

      await tx.wait();
      onProposalCreated();
      
    } catch (err) {
      console.error('Error creating proposal:', err);
      setError(err instanceof Error ? err.message : 'Failed to create proposal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-proposal-overlay">
      <div className="create-proposal-modal">
        <div className="modal-header">
          <h2>Create New Proposal</h2>
          <button className="close-btn" onClick={onClose} disabled={loading}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="proposal-form">
          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe what this proposal will do..."
              rows={4}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Actions</label>
            <div className="actions-list">
              {formData.targets.map((_, index) => (
                <div key={index} className="action-item">
                  <div className="action-inputs">
                    <div className="input-group">
                      <label>Target Address *</label>
                      <input
                        type="text"
                        value={formData.targets[index]}
                        onChange={(e) => handleInputChange('targets', e.target.value, index)}
                        placeholder="0x..."
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="input-group">
                      <label>Value (ETH)</label>
                      <input
                        type="number"
                        value={formData.values[index]}
                        onChange={(e) => handleInputChange('values', e.target.value, index)}
                        placeholder="0"
                        min="0"
                        step="0.001"
                        disabled={loading}
                      />
                    </div>
                    <div className="input-group">
                      <label>Calldata</label>
                      <input
                        type="text"
                        value={formData.calldatas[index]}
                        onChange={(e) => handleInputChange('calldatas', e.target.value, index)}
                        placeholder="0x..."
                        disabled={loading}
                      />
                    </div>
                  </div>
                  {formData.targets.length > 1 && (
                    <button
                      type="button"
                      className="remove-action-btn"
                      onClick={() => removeAction(index)}
                      disabled={loading}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              className="add-action-btn"
              onClick={addAction}
              disabled={loading}
            >
              + Add Action
            </button>
          </div>

          {error && (
            <div className="error">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Proposal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProposalForm;
